import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { useLeadLists } from "@/hooks/use-lead-lists";
import { useAssignableUsers } from "@/hooks/use-users";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useLeadListStats } from "@/hooks/use-lead-list-actions";
import { useUpdateLeadList } from "@/hooks/use-lead-list-actions";
import { SrIcon } from "@/components/ui/SrIcon";
import { formatPercent } from "@/lib/format";
import { listYield } from "@/lib/lead-stats-calc";
import LeadListFormDialog from "./LeadListFormDialog";
import ListLeadsDialog from "./ListLeadsDialog";
import NewLeadDialog from "./NewLeadDialog";

const SOURCE_LABELS = { csv: "CSV", brreg_search: "Brønnøysund", agent: "Agent", manual: "Manuelt", sheet: "Arket" } as const;
const SHARED = "__shared";

const pct = (v: number | null) => (v === null ? "–" : formatPercent(Math.round(v * 100)));

/**
 * Lister med statistikk (Leadlister): totalt, åpne, låst, i pipe, kunde, parkert, irrelevant,
 * ringt-andel og utbytte (andel i pipe eller kunde av de ringte, se listYield).
 */
export default function LeadListsTable() {
  const { data: lists, isLoading } = useLeadLists();
  const { data: stats } = useLeadListStats();
  const update = useUpdateLeadList();
  const { data: users } = useAssignableUsers();
  const [formOpen, setFormOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [openList, setOpenList] = useState<{ id: string; name: string } | null>(null);
  const statsById = new Map((stats ?? []).map((s) => [s.list_id, s]));

  return (
    <section aria-label="Leadlister" className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-xl font-semibold">Leadlister</h2>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setAdding(true)}>
            <SrIcon name="plus" className="mr-2 h-4 w-4" />
            Nytt lead
          </Button>
          <Button onClick={() => setFormOpen(true)}>Ny liste</Button>
        </div>
      </div>

      {isLoading ? (
        <Skeleton className="h-24 w-full" />
      ) : (lists ?? []).length === 0 ? (
        <p className="border border-border p-6 text-sm text-muted-foreground">
          Ingen lister ennå. Lag en liste fra en CSV-fil eller et Brønnøysund-søk, så kan møtebookerne begynne å ringe.
        </p>
      ) : (
        <div className="overflow-x-auto border-[1.5px] border-foreground">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs text-muted-foreground">
                <th className="p-2">Liste</th>
                <th className="p-2">Kilde</th>
                <th className="p-2">Eier</th>
                <th className="p-2 text-right">Totalt</th>
                <th className="p-2 text-right">Åpne</th>
                <th className="p-2 text-right">Låst</th>
                <th className="p-2 text-right">I pipe</th>
                <th className="p-2 text-right">Kunde</th>
                <th className="p-2 text-right">Parkert</th>
                <th className="p-2 text-right">Avvist</th>
                <th className="p-2 text-right">Ringt</th>
                <th className="p-2 text-right">Utbytte</th>
                <th className="p-2">Aktiv</th>
                <th className="p-2" />
              </tr>
            </thead>
            <tbody>
              {(lists ?? []).map((l) => {
                const s = statsById.get(l.id);
                const y = s ? listYield({ total: Number(s.total), called_count: Number(s.called_count), in_pipe_count: Number(s.in_pipe_count), customer_count: Number(s.customer_count) }) : null;
                return (
                  <tr key={l.id} className="border-b border-border/50">
                    <td className="p-2 font-medium">{l.name}</td>
                    <td className="p-2">{SOURCE_LABELS[l.source]}</td>
                    <td className="p-2">
                      <Select
                        value={l.owner_id ?? SHARED}
                        disabled={update.isPending}
                        onValueChange={(v) => update.mutate({ id: l.id, patch: { owner_id: v === SHARED ? null : v } })}
                      >
                        <SelectTrigger className="h-8 w-40" aria-label={`Eier av ${l.name}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={SHARED}>Felles</SelectItem>
                          {(users ?? []).map((u) => (
                            <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="p-2 text-right">{s?.total ?? 0}</td>
                    <td className="p-2 text-right">{s?.open_count ?? 0}</td>
                    <td className="p-2 text-right">{s?.locked_count ?? 0}</td>
                    <td className="p-2 text-right">{s?.in_pipe_count ?? 0}</td>
                    <td className="p-2 text-right">{s?.customer_count ?? 0}</td>
                    <td className="p-2 text-right">{s?.parked_count ?? 0}</td>
                    <td className="p-2 text-right">{s?.irrelevant_count ?? 0}</td>
                    <td className="p-2 text-right tabular-nums">{pct(y?.calledShare ?? null)}</td>
                    <td className="p-2 text-right tabular-nums">{pct(y?.yieldShare ?? null)}</td>
                    <td className="p-2">
                      <Switch
                        checked={l.is_active}
                        disabled={update.isPending}
                        aria-label={`${l.is_active ? "Deaktiver" : "Aktiver"} ${l.name}`}
                        onCheckedChange={(is_active) => update.mutate({ id: l.id, patch: { is_active } })}
                      />
                    </td>
                    <td className="p-2 text-right">
                      <Button variant="ghost" size="sm" onClick={() => setOpenList({ id: l.id, name: l.name })}>
                        Se leads
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <LeadListFormDialog open={formOpen} onOpenChange={setFormOpen} />
      <NewLeadDialog open={adding} onOpenChange={setAdding} />
      <ListLeadsDialog listId={openList?.id ?? null} listName={openList?.name ?? ""} onOpenChange={(o) => !o && setOpenList(null)} />
    </section>
  );
}
